import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { fetchMe, UnauthenticatedError } from './api';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectPage } from './pages/ProjectPage';
import { PullRequestsPage } from './pages/PullRequestsPage';
import { CiPage } from './pages/CiPage';
import { ActivityPage } from './pages/ActivityPage';
import { StatusPage } from './pages/StatusPage';
import { LoginPage } from './pages/LoginPage';
import { AdminPage } from './pages/AdminPage';
import { TasksPage } from './pages/TasksPage';
import { DocsGlobalPage } from './pages/DocsGlobalPage';
import { RisksPage } from './pages/RisksPage';
import { AutorizarMcpPage } from './pages/AutorizarMcpPage';
import { ConexionesPage } from './pages/ConexionesPage';

/**
 * Puerta de entrada: sin sesión válida, todo lleva a /login.
 *
 * La comprobación se hace contra la API, no contra nada guardado en el
 * navegador: el estado de sesión lo decide el servidor.
 */
function RequireSession({ children }: { children: JSX.Element }): JSX.Element {
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, retry: false, staleTime: 300_000 });
  const location = useLocation();

  if (me.isPending) return <Loading />;
  if (me.isError) {
    if (me.error instanceof UnauthenticatedError) {
      // Volver aquí después de entrar: así la pantalla de consentimiento de Claude, o
      // cualquier enlace del portal, sobrevive al login.
      const aqui = location.pathname + location.search;
      const volver = aqui === '/' ? '' : `?volver=${encodeURIComponent(aqui)}`;
      return <Navigate to={`/login${volver}`} replace />;
    }
    // Un error que no es de sesión (API caída) no debe empujar a login en
    // bucle: se muestra tal cual.
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-[13px]">
        No se pudo comprobar la sesión: {me.error.message}
      </div>
    );
  }
  return children;
}

/** La pantalla de administración necesita saber el rol para ocultar lo que no se puede usar. */
function AdminRoute(): JSX.Element {
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, retry: false, staleTime: 300_000 });
  if (me.isPending) return <Loading />;
  if (me.isError) return <Navigate to="/login" replace />;
  return <AdminPage me={me.data} />;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Sin el marco del portal: es una pantalla de paso, que llega desde Claude. */}
      <Route
        path="/autorizar-mcp"
        element={
          <RequireSession>
            <AutorizarMcpPage />
          </RequireSession>
        }
      />

      <Route
        element={
          <RequireSession>
            <Layout />
          </RequireSession>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:slug" element={<ProjectPage />} />
        <Route path="prs" element={<PullRequestsPage />} />
        <Route path="ci" element={<CiPage />} />
        <Route path="activity" element={<ActivityPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="docs" element={<DocsGlobalPage />} />
        <Route path="risks" element={<RisksPage />} />
        <Route path="status" element={<StatusPage />} />
        <Route path="admin" element={<AdminRoute />} />
        <Route path="conexiones" element={<ConexionesPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
